import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { withOfflineListCache } from '@/lib/mobile/offline-db/list-cache';
import { nativeOfflineQueryOptions } from '@/lib/mobile/offline-db/query-options';
import { isNativePlatform } from '@/lib/mobile/platform';
import { stableStringify } from '@/lib/utils/stable-stringify';

import type { Database } from '@/integrations/supabase/types';

export type NotificationType = Database['public']['Enums']['notification_type'];

export type NotificationCategory = Database['public']['Enums']['notification_category'];

/**
 * Every column {@link Notification} declares, named explicitly. Kept in sync
 * with that interface by hand rather than fetched with '*': these rows carry
 * user-facing title/message content and now land in the on-disk offline
 * cache, so widening what reaches the device must be a decision, not a
 * side effect of a migration.
 *
 * `metadata` is deliberately absent. The producer packs constituent detail
 * into it - sender name and address, email subject, rejection reason - and no
 * consumer of this list reads it, so selecting it wrote third-party personal
 * data to an encrypted store on a phone to render nothing.
 */
const NOTIFICATION_COLUMNS = `
  id,
  organization_id,
  recipient_user_id,
  sender_user_id,
  notification_type,
  category,
  title,
  message,
  action_url,
  related_entity_type,
  related_entity_id,
  read_at,
  dismissed_at,
  saved_at,
  created_at,
  updated_at
`;

export interface Notification {
  id: string;
  organization_id: string;
  recipient_user_id: string;
  sender_user_id: string | null;
  notification_type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  action_url: string | null;
  related_entity_type: string | null;
  related_entity_id: string | null;
  read_at: string | null;
  dismissed_at: string | null;
  saved_at: string | null;
  created_at: string;
  updated_at: string;
  sender?: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    avatar_url: string | null;
  };
}

interface UseNotificationsOptions {
  category?: NotificationCategory;
  unreadOnly?: boolean;
  includeDismissed?: boolean;
  doneOnly?: boolean;
  savedOnly?: boolean;
  limit?: number;
  offset?: number;
}

/** Every dimension that changes WHICH notifications a fetch returns. */
interface NotificationsView {
  readonly organizationId: string;
  readonly recipientUserId: string;
  readonly category: NotificationCategory | undefined;
  readonly unreadOnly: boolean;
  readonly includeDismissed: boolean;
  readonly doneOnly: boolean;
  readonly savedOnly: boolean;
  readonly limit: number;
  readonly offset: number;
}

/**
 * One page of notifications.
 *
 * `totalCount` is `number | undefined` in the TYPE, not just in one branch's
 * inferred shape: a cache hit never runs the server's count, and ADR 0067
 * says a count comes from the server or not at all. Declaring it here stops
 * the "offline means unknown" contract from living in a comment that the next
 * refactor quietly re-narrows to `number`.
 */
export interface NotificationsPage {
  readonly items: Notification[];
  readonly totalCount: number | undefined;
}

/** sync_state scope for the cached notification page. */
const NOTIFICATIONS_OFFLINE_SCOPE = 'notifications';

/**
 * Identity of one cached notifications view. Every filter is in it, plus the
 * page window and a stamp of the cached row SHAPE: the family holds ONE view
 * at a time, so a signature missing a dimension would serve the inbox's rows
 * as the saved list. `stableStringify` rather than `JSON.stringify` because
 * two identical option sets built by different call sites must read as one
 * view instead of pointlessly purging each other on every switch.
 */
export function notificationsViewSignature(view: NotificationsView): string {
  // The shape stamp goes LAST: spread first and a future `shape` field on the
  // view would overwrite it, silently disabling shape versioning.
  return stableStringify({ ...view, shape: 'notifications:v1' });
}

async function fetchNotificationsPage(
  view: NotificationsView,
): Promise<{ items: Notification[]; totalCount: number }> {
  let query = supabase
    .from('notifications')
    // Explicit column list, not '*': these rows carry user-facing title and
    // message content, and this hook now writes them to the ON-DISK offline
    // cache. A '*' would let a future column reach the device without anyone
    // deciding it should. The list mirrors the Notification interface exactly.
    .select(NOTIFICATION_COLUMNS, { count: 'exact' })
    .eq('organization_id', view.organizationId)
    .eq('recipient_user_id', view.recipientUserId)
    // Mirrored by compareCachedNotifications below — the two move together.
    .order('created_at', { ascending: false })
    .range(view.offset, view.offset + view.limit - 1);

  if (view.savedOnly) {
    // Saved view: only bookmarked notifications (regardless of dismissed state)
    query = query.not('saved_at', 'is', null);
  } else if (view.doneOnly) {
    // Done view: only dismissed notifications
    query = query.not('dismissed_at', 'is', null);
  } else if (!view.includeDismissed) {
    // Inbox view (default): only non-dismissed
    query = query.is('dismissed_at', null);
  }

  if (view.category) {
    query = query.eq('category', view.category);
  }

  if (view.unreadOnly) {
    query = query.is('read_at', null);
  }

  const { data, count, error } = await query;

  if (error) {
    throw error;
  }

  return { items: (data || []) as Notification[], totalCount: count ?? 0 };
}

/** The cache read's stand-in for the server's `created_at DESC`. */
function compareCachedNotifications(left: Notification, right: Notification): number {
  return right.created_at.localeCompare(left.created_at);
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();

  const {
    category,
    unreadOnly = false,
    includeDismissed = false,
    doneOnly = false,
    savedOnly = false,
    limit = 50,
    offset = 0,
  } = options;

  return useQuery({
    queryKey: [
      'notifications',
      currentOrganizationId,
      user?.id,
      category,
      unreadOnly,
      includeDismissed,
      doneOnly,
      savedOnly,
      limit,
      offset,
    ],
    queryFn: async (context): Promise<NotificationsPage> => {
      if (!user?.id || !currentOrganizationId || currentOrganizationId === 'admin') {
        return { items: [], totalCount: 0 };
      }

      // READING `context.signal` is what flips TanStack's abortSignalConsumed
      // flag, and that flag decides what losing the last observer mid-fetch
      // does: cancel-and-revert, or let the in-flight request land in cache.
      // Only the native write-through has any use for the signal, so web is
      // left on the behaviour it had before this cache existed.
      const signal = isNativePlatform() ? context.signal : undefined;

      const view: NotificationsView = {
        organizationId: currentOrganizationId,
        recipientUserId: user.id,
        category,
        unreadOnly,
        includeDismissed,
        doneOnly,
        savedOnly,
        limit,
        offset,
      };

      // The server's exact count for this page, recorded only where it is
      // known: inside the network fetch. A cache hit never runs it, so
      // `totalCount` comes back undefined rather than being derived from the
      // rows on hand — ADR 0067, counts come from the server or not at all.
      // Consumers already read it as `totalCount ?? 0`, which collapses
      // pagination to the single page the cache can actually serve instead of
      // offering pages no offline fetch could ever return.
      let serverTotalCount: number | undefined;

      const items = await withOfflineListCache<Notification>({
        ownerUserId: user.id,
        organizationId: currentOrganizationId,
        table: 'notifications',
        scope: NOTIFICATIONS_OFFLINE_SCOPE,
        viewSignature: notificationsViewSignature(view),
        getRowId: (notification) => notification.id,
        // An empty page must not clobber a good cache: offline it would read
        // as a convincing "all caught up" where "you are offline" is the truth.
        shouldCache: (list) => list.length > 0,
        compareCached: compareCachedNotifications,
        signal,
        fetchFromServer: async () => {
          const page = await fetchNotificationsPage(view);
          serverTotalCount = page.totalCount;
          return page.items;
        },
      });

      return { items, totalCount: serverTotalCount };
    },
    enabled: !!user?.id && !!currentOrganizationId && currentOrganizationId !== 'admin',
    staleTime: 30 * 1000, // 30 seconds
    // Avoids the flash of empty state on filter and page transitions, but only
    // WITHIN one org and user: the query key carries both, and keeping the
    // previous page across an org switch would render one organisation's
    // notification titles under another's - for as long as the replacement
    // query stays pending, which offline can be a while.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === currentOrganizationId && previousQuery.queryKey[2] === user?.id
        ? previous
        : undefined,
    // Native only, and 'always': a page served from the offline cache gets
    // dataUpdatedAt stamped NOW, so a stale-gated remount would keep serving
    // it long after the connection came back. Web keeps the app-wide default
    // (false) pinned in App.tsx — Realtime owns freshness there.
    refetchOnMount: isNativePlatform() ? 'always' : false,
    // networkMode + refetchOnReconnect, native-aware. See the helper for why
    // each value is what it is; both are load-bearing for the cache above.
    ...nativeOfflineQueryOptions(),
  });
}

/**
 * Lightweight query that returns the IDs of every read-but-not-dismissed
 * notification for the current user/org — no row limit. Used by the
 * "Dismiss all read" bulk action so it operates on the full inbox, not
 * just the slice rendered in the popover.
 */
export function useReadDismissibleNotificationIds() {
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();

  return useQuery({
    queryKey: ['notifications', 'read-dismissible-ids', currentOrganizationId, user?.id],
    queryFn: async () => {
      if (!user?.id || !currentOrganizationId || currentOrganizationId === 'admin') {
        return [];
      }

      const { data, error } = await supabase
        .from('notifications')
        .select('id')
        .eq('organization_id', currentOrganizationId)
        .eq('recipient_user_id', user.id)
        .not('read_at', 'is', null)
        .is('dismissed_at', null);

      if (error) {
        throw error;
      }

      return (data ?? []).map((row) => row.id);
    },
    enabled: !!user?.id && !!currentOrganizationId && currentOrganizationId !== 'admin',
    staleTime: 30 * 1000,
  });
}

export function useUnreadNotificationCount() {
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();

  return useQuery({
    queryKey: ['notifications', 'unread-count', currentOrganizationId, user?.id],
    queryFn: async () => {
      if (!user?.id || !currentOrganizationId || currentOrganizationId === 'admin') {
        return 0;
      }

      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', currentOrganizationId)
        .eq('recipient_user_id', user.id)
        .is('read_at', null)
        .is('dismissed_at', null);

      if (error) {
        throw error;
      }

      return count || 0;
    },
    enabled: !!user?.id && !!currentOrganizationId && currentOrganizationId !== 'admin',
    staleTime: 30 * 1000,
  });
}
